/* eslint-disable no-constant-condition */
import PropTypes from 'prop-types';
import { useState, useMemo, useCallback, useRef, Fragment } from 'react';
import ExperienceWalletForm from 'components/forms/experienceWallet/ExperienceWalletForm';
import { Link, useHistory } from 'react-router-dom';
import { pathOr } from 'ramda';

/* i18n */
import { useIntl, FormattedMessage } from 'react-intl';

/* Styles */
import recStyles from 'styles/recommendations-list.module.scss';

/* Analytics */
import TimeMe from 'timeme.js';

/* Assets */
import info from 'assets/images/Info_Cards.svg';

/* API */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from 'lib/API';

/* Analytics */
import { trackEvent } from 'lib/analytics';

/* Material UI and other UI Dependencies */
import {
  Container,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Hidden,
  Alert,
} from '@mui/material';
import { FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';

/* UI Components */
import Loading from 'components/Loading';
import MobilePaginatedDrawers from './forms/experienceWallet/MobilePaginatedDrawers';
import AddNewExperienceMenu from './forms/experienceWallet/AddNewExperienceMenu';
import ExperienceWalletDesktopTable from './forms/experienceWallet/ExperienceWalletDesktopTable';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import QrCodeIcon from '@mui/icons-material/QrCode';
import FeatureRequestSurveyScript from './FeatureRequestSurveyScript';
import CustomNotification from './CustomNotification';
import { ConnectCredWalletDialog } from './ConnectCredWalletDialog';
import LoadingModal from './LoadingModal';
import { useUpdateUser } from 'lib/hooks/userQueries';
import { BRANCH_OPTIONS } from 'constants/militaryOptions';
import { EDUCATION_LEVEL_BY_ID, EDUCATION_LEVEL_OPTIONS } from 'constants/educationLevelOptions';
import { jstClepTitles, jstDlptLanguages } from 'components/JSTcomponents/jstSectionsConfig';
import { getRankLabel, getTypeLabelId, lcwTypeLabels } from './forms/experienceWallet/ExperienceWalletDrawer';

const emptyExperience = {
  type: '',
  level: null,
  field: null,
  skills: [],
  description: '',
  employer_provider: '',
  start_date: null,
  end_date: null,
  comments: null,
  // Empty so the civilian/military chooser opens with nothing pre-selected
  experienceCategory: '',
};

let experienceAlertShownThisSession = false;

// Modal only applies to accounts that existed before this feature rolled out
const EXPERIENCE_ALERT_ROLLOUT_DATE = new Date('2026-09-17T00:00:00');

/**
 * Parse the /users/me `timestamp` field (format YYYYMMDDHHMMSS) into a Date.
 * Returns null for missing or unparsable values.
 */
const parseAccountTimestamp = (value) => {
  if (!value || value.length < 8) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6)) - 1;
  const day = Number(value.slice(6, 8));
  const hour = Number(value.slice(8, 10) || 0);
  const minute = Number(value.slice(10, 12) || 0);
  const second = Number(value.slice(12, 14) || 0);
  const date = new Date(year, month, day, hour, minute, second);
  return Number.isFinite(date.getTime()) ? date : null;
};

/**
 * Safely parse a date string into a comparable timestamp.
 * Returns null for missing or unparsable values.
 */
const toTimestamp = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * Build a comparator for a given date field. Missing or null values:
 *  - For `end_date`: treat as "Present" (sort first when descending, last when ascending).
 *  - For `start_date`: treat as missing (sort last regardless of direction) so
 *    placeholder rows do not float above real data.
 */
const dateComparator = (field, direction) => (a, b) => {
  const aTs = toTimestamp(a[field]);
  const bTs = toTimestamp(b[field]);

  if (aTs === null && bTs === null) return 0;

  if (field === 'end_date') {
    if (aTs === null) return direction === 'asc' ? 1 : -1;
    if (bTs === null) return direction === 'asc' ? -1 : 1;
  } else {
    if (aTs === null) return 1;
    if (bTs === null) return -1;
  }

  return direction === 'asc' ? aTs - bTs : bTs - aTs;
};

const sortByMostRecent = (a, b) => dateComparator('end_date', 'desc')(a, b);
const sortByMostOldest = (a, b) => dateComparator('end_date', 'asc')(a, b);
const sortBySkillsAsc = (a, b) => (a.skills?.length || 0) - (b.skills?.length || 0);
const sortBySkillsDesc = (a, b) => (b.skills?.length || 0) - (a.skills?.length || 0);
const sortByFieldAsc = (a, b) => {
  const at = a.field?.title;
  const bt = b.field?.title;
  if (!at && !bt) return 0;
  if (!at) return 1;
  if (!bt) return -1;
  return at.localeCompare(bt);
};
const sortByFieldDesc = (a, b) => {
  const at = a.field?.title;
  const bt = b.field?.title;
  if (!at && !bt) return 0;
  if (!at) return 1;
  if (!bt) return -1;
  return bt.localeCompare(at);
};
const isWalletImported = (origin) =>
  Array.isArray(origin) && origin.some((o) => o === 'ler' || o === 'ExperienceOriginEnum.ler');

const displayValueSort = (getValue, direction) => (a, b) => {
  const at = String(getValue(a) || '').trim();
  const bt = String(getValue(b) || '').trim();

  if (!at && !bt) return 0;
  if (!at) return 1;
  if (!bt) return -1;

  return direction === 'asc' ? at.localeCompare(bt) : bt.localeCompare(at);
};

const getFieldDisplayValue = (item) => item.field?.title || item.field?.soc_title || item.field?.moc_title || '';

const getExperienceTypeDisplayValue = (item, intl) => {
  const rankLabel = getRankLabel(item);
  if (rankLabel) return rankLabel;

  const degreeLevelOption =
    item.type === 'degree' && item.level != null ? EDUCATION_LEVEL_BY_ID.get(Number(item.level)) : null;
  if (degreeLevelOption) return intl.formatMessage({ id: degreeLevelOption.labelId });

  const typeLabelId = getTypeLabelId(item);
  if (typeLabelId) return intl.formatMessage({ id: typeLabelId });

  return lcwTypeLabels[item.type] ?? item.type ?? '';
};
const genericSort = (key, direction) => (a, b) => {
  if (!a[key] && !b[key]) {
    return 0;
  }
  if (direction === 'asc') {
    if (a[key] && !b[key]) {
      return -1;
    }
    if (b[key] && !a[key]) {
      return 1;
    }
  }
  if (direction === 'desc') {
    if (b[key] && !a[key]) {
      return -1;
    }
    if (a[key] && !b[key]) {
      return 1;
    }
  }

  if (direction === 'asc') {
    return a[key].localeCompare(b[key]);
  }
  return b[key].localeCompare(a[key]);
};

const Experience = () => {
  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [refetchCounter, setRefetchCounter] = useState(0);
  const [sortBy, setSortBy] = useState('default');
  const [sortDirection, setSortDirection] = useState('desc');
  const [newExperienceOpen, setNewExperienceOpen] = useState(false);
  const [formScope, setFormScope] = useState('work');
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(-1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasSelectedCategory, setHasSelectedCategory] = useState(false);
  const [alertDismissed, setAlertDismissed] = useState(experienceAlertShownThisSession);
  const analyticsInitialized = useRef(false);
  const updateUser = useUpdateUser();

  /*-- initial load --*/
  const experienceQuery = useQuery({
    queryKey: ['/users/me/experience/'],
    refetchOnMount: 'always',
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });
  const contactQuery = useQuery({
    queryKey: ['/users/me'],
    cacheTime: 0,
    refetchOnWindowFocus: false,
  });
  const queryClient = useQueryClient();

  /*-- analytics: run once on first render via ref guard --*/
  if (!analyticsInitialized.current) {
    analyticsInitialized.current = true;
    document.title = intl.formatMessage({ id: 'experience.page.altTitle' });
    TimeMe.stopTimer();
    TimeMe.setCurrentPageName('SKILLS');
    TimeMe.startTimer();
  }

  // Only for accounts created before the rollout date; new signups never see this modal
  const experiencePageVisited = pathOr(false, ['data', 'wp3_experience_page_visited'], contactQuery);
  const accountCreatedAt = parseAccountTimestamp(pathOr(null, ['data', 'timestamp'], contactQuery));
  const isOldUser = accountCreatedAt !== null && accountCreatedAt < EXPERIENCE_ALERT_ROLLOUT_DATE;
  const showExperienceAlert = isOldUser && !experiencePageVisited && !alertDismissed;

  const dismissExperienceAlert = () => {
    experienceAlertShownThisSession = true;
    setAlertDismissed(true);
    updateUser.mutate({ wp3_experience_page_visited: true });
  };

  const hasMilitaryService =
    experienceQuery.isSuccess &&
    (experienceQuery.data?.current_military_service === true ||
      experienceQuery.data?.previous_military_service === true);

  /**
   * Merge work (job_experience), education, and military (jst.military_experience)
   * into a single unified list. Each item gets a `sourceType` field for icon/label mapping.
   */
  const mergedExperience = useMemo(() => {
    if (experienceQuery.isFetching || !experienceQuery.isSuccess) return [];

    const data = experienceQuery.data;
    const items = [];

    // Work experiences from job_experience
    (data?.job_experience || []).forEach((exp, i) => {
      items.push({
        ...exp,
        field: exp.fields?.[0] ?? null,
        id: `work-${i}`,
        sourceType: 'work',
        // Prod schema: description = focus/role/major text, comments = tell-me-about text, title = null.
        title: exp.description ?? null,
        comments: exp.comments ?? null,
        wallet: isWalletImported(exp.origin),
      });
    });

    // JST: Military Courses
    const jst = data?.jst;
    const jstBranch = jst?.personal?.branch || '';
    const jstBranchOption = BRANCH_OPTIONS.find(
      (b) => b.apiValue === jstBranch || b.value === jstBranch || b.backendName === jstBranch
    );
    const jstBranchDisplay = jstBranchOption?.backendName || jstBranch;

    (jst?.military_courses || []).forEach((mc, i) => {
      items.push({
        id: `jst-mc-${i}`,
        // Military courses live in jst.military_courses but are fully editable and
        // deletable from this page (not `isJst`, which is read-only/redirect-only).
        isMilitaryCourse: true,
        militaryCourseIndex: i,
        military_course_id: mc.military_course_id ?? null,
        // Keep the complete stored object so edits merge onto it and JST-only
        // fields (ace_identifier, credit_area, description, location…) are never lost.
        _raw: mc,
        sourceType: 'education',
        type: 'militaryCourse',
        title: mc.course_title || '',
        employer_provider: mc.institution_provider || jstBranchDisplay,
        skills: mc.parsed_skills || [],
        start_date: mc.start_date || null,
        end_date: mc.end_date || null,
        field: null,
        is_current: mc.currently_enrolled ?? false,
        comments: mc.description || null,
      });
    });

    // JST: Military Experience
    (jst?.military_experience || []).forEach((me, i) => {
      // Resolve branch per-entry: use the entry's own branch_of_service first,
      // fall back to jst.personal.branch so entries added via ExperienceWallet
      // display the branch the user actually selected in the form.
      const entryBranch = me.branch_of_service || jstBranch;
      const entryBranchOption = BRANCH_OPTIONS.find(
        (b) => b.apiValue === entryBranch || b.value === entryBranch || b.backendName === entryBranch
      );
      const entryBranchDisplay = entryBranchOption?.backendName || entryBranch;

      items.push({
        id: `jst-me-${i}`,
        isJst: true,
        sourceType: 'military',
        type: 'militaryExperience',
        title: me.experience_title || '',
        rank: me.rank || '',
        employer_provider: entryBranchDisplay,
        skills: me.parsed_skills || [],
        start_date: me.start_date || null,
        end_date: me.end_date || null,
        // The backend military_experience schema only persists experience_title and
        // occupation_id (no moc_code/moc_title/soc) — rebuild the MilitaryMOCAutocomplete
        // value from those so editing pre-fills the saved job title.
        field: me.occupation_id
          ? {
              moc_code: '',
              moc_title: me.experience_title || '',
              soc_title: '',
              soc_code: me.occupation_id,
            }
          : null,
        occupation_id: me.occupation_id || '',
        soc_id: me.id || null,
        branch_of_service: entryBranchOption?.backendName || entryBranch,
        branch: entryBranchOption?.value || '',
        experienceCategory: 'military',
        is_current: me.currently_enrolled === true,
        comments: me.description || null,
      });
    });

    // JST: Other Learning Experiences
    (jst?.other_learning || []).forEach((ol, i) => {
      items.push({
        id: `jst-ol-${i}`,
        isJst: true,
        sourceType: 'education',
        type: 'otherLearning',
        title: ol.title || '',
        employer_provider: ol.institution_provider || '',
        skills: ol.parsed_skills || [],
        start_date: ol.date_taken || null,
        end_date: ol.date_taken || null,
        field: null,
        is_current: false,
        comments: null,
      });
    });

    // JST: Academic Courses
    (jst?.academic_courses || []).forEach((ac, i) => {
      items.push({
        id: `jst-ac-${i}`,
        isJst: true,
        sourceType: 'education',
        type: 'academicCourse',
        title: ac.title || '',
        employer_provider: ac.institution?.name || '',
        skills: ac.parsed_skills || [],
        start_date: ac.start_date || null,
        end_date: ac.end_date || null,
        field: null,
        is_current: ac.currently_enrolled === true,
        comments: null,
      });
    });

    // JST: Academic Degrees/Certificates
    (jst?.academic_degrees_certificates || []).forEach((ad, i) => {
      // Show the credential the user actually picked ("Associate Degree",
      // "Certificate") rather than the generic "Academic Degree/Certificate".
      // Education levels map to type 'degree' + a numeric level, exactly like
      // education[] rows, so the dropdown reselects them when editing.
      const credential = ad.education_experience_credential || '';
      const levelOption = EDUCATION_LEVEL_OPTIONS.find((o) => o.value === credential);
      items.push({
        id: `jst-ad-${i}`,
        // Editable and deletable in place from this page (not `isJst`, which is
        // redirect-only), saved back to jst.academic_degrees_certificates.
        isAcademicDegree: true,
        academicDegreeIndex: i,
        // Keep the stored object so edits merge onto it and any JST-only field
        // without a form input survives.
        _raw: ad,
        sourceType: 'education',
        type: levelOption ? 'degree' : credential || 'academicDegree',
        level: levelOption ? levelOption.id : null,
        title: ad.name || '',
        employer_provider: ad.institution_provider || '',
        skills: ad.skills_gained || [],
        start_date: ad.start_date || null,
        end_date: ad.end_date || null,
        field: null,
        is_current: ad.currently_enrolled === true,
        comments: null,
      });
    });

    // JST: College Level Test Scores (CLEP, DSST, DLPT) — displayed as Assessments
    const clepDsstProviderLabel = {
      clep: 'College Level Examination Program (CLEP)',
      dsst: 'DANTES Subject Standardized Tests (DSST)',
    };
    const clepTitleLookup = Object.fromEntries(jstClepTitles.map((opt) => [opt.value, opt.label]));
    const dlptTitleLookup = Object.fromEntries(jstDlptLanguages.map((opt) => [opt.value, opt.label]));

    (jst?.clep_and_dsst_test_scores || []).forEach((ts, i) => {
      items.push({
        id: `jst-clep-${i}`,
        isJst: true,
        sourceType: 'education',
        type: 'assessment',
        title: clepTitleLookup[ts.title] || ts.title || '',
        employer_provider: clepDsstProviderLabel[ts.type] || ts.type || '',
        skills: [],
        start_date: ts.date_taken || null,
        end_date: ts.date_taken || null,
        field: null,
        is_current: false,
        comments: ts.students_score ? `Score: ${ts.students_score}` : null,
      });
    });

    (jst?.dlpt_test_scores || []).forEach((ts, i) => {
      items.push({
        id: `jst-dlpt-${i}`,
        isJst: true,
        sourceType: 'education',
        type: 'assessment',
        title: dlptTitleLookup[ts.title] || ts.title || '',
        employer_provider: 'Defense Language Proficiency Test (DLPT)',
        skills: [],
        start_date: ts.date_taken || null,
        end_date: ts.date_taken || null,
        field: null,
        is_current: false,
        comments: ts.students_score ? `Score: ${ts.students_score}` : null,
      });
    });

    return items;
  }, [experienceQuery.data, experienceQuery.isFetching, experienceQuery.isSuccess]);

  /** Sorted experience derived from mergedExperience, sortBy, and sortDirection */
  const experience = useMemo(() => {
    if (!mergedExperience.length) return mergedExperience;

    const sorted =
      sortBy === 'experience'
        ? mergedExperience
            .slice()
            .sort(displayValueSort((item) => getExperienceTypeDisplayValue(item, intl), sortDirection))
        : sortBy === 'description'
        ? mergedExperience.slice().sort(displayValueSort((item) => item.title, sortDirection))
        : sortBy === 'default'
        ? mergedExperience.slice().sort(sortDirection === 'asc' ? sortByMostOldest : sortByMostRecent)
        : sortBy === 'start_date'
        ? mergedExperience.slice().sort(dateComparator('start_date', sortDirection))
        : sortBy === 'end_date'
        ? mergedExperience.slice().sort(dateComparator('end_date', sortDirection))
        : sortBy === 'field'
        ? mergedExperience.slice().sort(displayValueSort(getFieldDisplayValue, sortDirection))
        : sortBy === 'numberSkills'
        ? mergedExperience.slice().sort(sortDirection === 'asc' ? sortBySkillsAsc : sortBySkillsDesc)
        : sortBy === 'provider'
        ? mergedExperience.slice().sort(displayValueSort((item) => item.employer_provider, sortDirection))
        : mergedExperience;

    return sorted;
  }, [mergedExperience, sortBy, sortDirection, intl]);

  /**
   * Items handled by the generic job_experience/education/military save path.
   * Excludes read-only JST rows and military courses (which have their own
   * jst.military_courses save/delete path below).
   */
  const nonJstExperience = useMemo(
    () =>
      experience.filter(
        (item) => (!item.isJst || item.sourceType === 'military') && !item.isMilitaryCourse && !item.isAcademicDegree
      ),
    [experience]
  );

  /**
   * PUT the full jst blob back with a new academic_degrees_certificates array.
   * Read-modify-write of the whole jst object, so no other section is wiped.
   */
  const putAcademicDegrees = useCallback(
    (nextDegrees) => {
      const jst = experienceQuery.data?.jst || {};
      return apiRequest('PUT', '/users/me/experience/', {
        data: { jst: { ...jst, academic_degrees_certificates: nextDegrees } },
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });
      });
    },
    [experienceQuery.data, queryClient]
  );

  /**
   * Add or update a single academic degree/certificate, merged onto the stored
   * object. Matches by array index; appends when there is no match.
   */
  const upsertAcademicDegree = useCallback(
    (degree, { index = -1 } = {}) => {
      const existing = experienceQuery.data?.jst?.academic_degrees_certificates || [];
      const matchIdx = typeof index === 'number' && index >= 0 && index < existing.length ? index : -1;
      const nextDegrees = matchIdx >= 0 ? existing.map((d, i) => (i === matchIdx ? degree : d)) : [...existing, degree];
      return putAcademicDegrees(nextDegrees);
    },
    [experienceQuery.data, putAcademicDegrees]
  );

  /**
   * PUT the full jst blob back with a new military_courses array. Sending the
   * whole jst object (read-modify-write) guarantees no other jst section is
   * wiped, regardless of how the backend merges the top-level `jst` key.
   */
  const putMilitaryCourses = useCallback(
    (nextCourses) => {
      const jst = experienceQuery.data?.jst || {};
      return apiRequest('PUT', '/users/me/experience/', {
        data: { jst: { ...jst, military_courses: nextCourses } },
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });
      });
    },
    [experienceQuery.data, queryClient]
  );

  /**
   * Add or update a single military course, merged onto the stored object.
   * Matches an existing record by military_course_id (or array index for a
   * not-yet-saved record); appends when there is no match.
   */
  const upsertMilitaryCourse = useCallback(
    (course, { courseId = null, index = -1 } = {}) => {
      const existing = experienceQuery.data?.jst?.military_courses || [];
      let matchIdx = -1;
      if (courseId != null) {
        matchIdx = existing.findIndex((c) => c.military_course_id === courseId);
      }
      if (matchIdx < 0 && typeof index === 'number' && index >= 0 && index < existing.length) {
        matchIdx = index;
      }
      const nextCourses = matchIdx >= 0 ? existing.map((c, i) => (i === matchIdx ? course : c)) : [...existing, course];
      return putMilitaryCourses(nextCourses);
    },
    [experienceQuery.data, putMilitaryCourses]
  );

  const handleRedirect = useCallback(
    (route) => {
      history.push(`${urlPrefix}/${route}`);
    },
    [history, urlPrefix]
  );

  // Open the new-experience dialog. Work scope resolves to the military scope when
  // the user has military service, so the form shows the civilian/military chooser.
  const handleAddExperience = (scope) => {
    setFormScope(scope === 'work' && hasMilitaryService ? 'military' : scope);
    setHasSelectedCategory(false);
    setNewExperienceOpen(true);
  };

  const replaceItem = useCallback(() => {
    // After a PUT save, refetch the experience data from the server
    queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });
  }, [queryClient]);

  const appendItem = useCallback(() => {
    // After a PUT save, refetch the experience data from the server
    queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });

    enqueueSnackbar('', {
      variant: 'default',
      content: (key) => (
        <CustomNotification id={key}>
          {intl.formatMessage(
            { id: 'experience.successfulAdd' },
            {
              jobsLink: (chunks) => <ToastLink title={chunks} onClick={() => handleRedirect('jobs')} />,
              careersLink: (chunks) => <ToastLink title={chunks} onClick={() => handleRedirect('careers')} />,
              trainingLink: (chunks) => <ToastLink title={chunks} onClick={() => handleRedirect('training')} />,
            }
          )}
        </CustomNotification>
      ),
    });
  }, [queryClient, enqueueSnackbar, intl, handleRedirect]);

  /**
   * Delete an experience item by index.
   * Rebuilds the full payload from current data, removes the target item,
   * then sends PUT to persist the change.
   */
  const deleteItem = useCallback(
    (index) => {
      const item = experience[index];
      if (!item || (item.isJst && item.sourceType !== 'military')) return;

      // Military courses delete via the jst.military_courses path, not the
      // generic job_experience/education/military rebuild.
      if (item.isMilitaryCourse) {
        const existing = experienceQuery.data?.jst?.military_courses || [];
        let matchIdx = -1;
        if (item.military_course_id != null) {
          matchIdx = existing.findIndex((c) => c.military_course_id === item.military_course_id);
        }
        if (matchIdx < 0 && typeof item.militaryCourseIndex === 'number') {
          matchIdx = item.militaryCourseIndex;
        }
        const nextCourses = matchIdx >= 0 ? existing.filter((_, i) => i !== matchIdx) : existing;
        setIsDeleting(true);
        putMilitaryCourses(nextCourses)
          .then(() => {
            enqueueSnackbar('', {
              variant: 'default',
              content: (key) => (
                <CustomNotification id={key}>
                  {intl.formatMessage({ id: 'experience.deleteSuccess' })}
                </CustomNotification>
              ),
            });
          })
          .catch(() => {
            enqueueSnackbar(intl.formatMessage({ id: 'experience.deleteError' }), { variant: 'error' });
          })
          .finally(() => {
            setIsDeleting(false);
          });
        return;
      }

      // Academic degrees/certificates delete via the jst.academic_degrees_certificates
      // path, the same way military courses do.
      if (item.isAcademicDegree) {
        const existing = experienceQuery.data?.jst?.academic_degrees_certificates || [];
        const matchIdx = typeof item.academicDegreeIndex === 'number' ? item.academicDegreeIndex : -1;
        const nextDegrees = matchIdx >= 0 ? existing.filter((_, i) => i !== matchIdx) : existing;
        setIsDeleting(true);
        putAcademicDegrees(nextDegrees)
          .then(() => {
            enqueueSnackbar('', {
              variant: 'default',
              content: (key) => (
                <CustomNotification id={key}>
                  {intl.formatMessage({ id: 'experience.deleteSuccess' })}
                </CustomNotification>
              ),
            });
          })
          .catch(() => {
            enqueueSnackbar(intl.formatMessage({ id: 'experience.deleteError' }), { variant: 'error' });
          })
          .finally(() => {
            setIsDeleting(false);
          });
        return;
      }

      const data = experienceQuery.data;

      let sourceIndex;
      if (item.sourceType === 'work') {
        sourceIndex = parseInt(item.id.replace('work-', ''), 10);
      } else if (item.sourceType === 'military') {
        sourceIndex = item.id.startsWith('jst-me-')
          ? parseInt(item.id.replace('jst-me-', ''), 10)
          : (experienceQuery.data?.jst?.military_experience || []).findIndex((_, idx) => `jst-me-${idx}` === item.id);
      }

      const buildPayload = () => {
        const jobExperiences = [...(data?.job_experience || [])];
        const militaryExperiences = [...(data?.jst?.military_experience || [])];

        if (item.sourceType === 'work') {
          jobExperiences.splice(sourceIndex, 1);
        } else if (item.sourceType === 'military') {
          militaryExperiences.splice(sourceIndex, 1);
        }

        return {
          job_experience: jobExperiences,
          jst: {
            ...(data?.jst || {}),
            military_experience: militaryExperiences,
          },
        };
      };

      const payload = buildPayload();

      setIsDeleting(true);
      apiRequest('PUT', '/users/me/experience/', { data: payload })
        .then(() => {
          queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });
          enqueueSnackbar('', {
            variant: 'default',
            content: (key) => (
              <CustomNotification id={key}>{intl.formatMessage({ id: 'experience.deleteSuccess' })}</CustomNotification>
            ),
          });
        })
        .catch(() => {
          enqueueSnackbar(intl.formatMessage({ id: 'experience.deleteError' }), { variant: 'error' });
        })
        .finally(() => {
          setIsDeleting(false);
        });
    },
    [experience, experienceQuery.data, queryClient, enqueueSnackbar, intl, putMilitaryCourses, putAcademicDegrees]
  );

  const matchFeatureValue =
    !contactQuery.isLoading && !contactQuery.isFetching
      ? pathOr(false, ['data', 'user_match_feature'], contactQuery)
      : false;

  // App.js keeps a persistent '/users/me/experience/' query alive for the whole session,
  // so by the time this page mounts, isLoading is already false (data is cached). But this
  // page also forces `refetchOnMount: 'always'` to get fresh data, so without checking
  // isFetching too, the page would render stale/empty content with no spinner while the
  // background refetch (which can take a few seconds) is in flight.
  if (experienceQuery.isLoading || experienceQuery.isFetching || contactQuery.isLoading) return <Loading />;

  return (
    <div className={recStyles.content}>
      <Container maxWidth="lg">
        <FeatureRequestSurveyScript />
        <Box>
          <Typography variant="h1">{intl.formatMessage({ id: 'experience.page.altTitle' })}</Typography>
          {!contactQuery.isFetching && (
            <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
              <Typography variant="body2">{intl.formatMessage({ id: 'experience.page.altSubTitle' })}</Typography>
              <Typography variant="body2" sx={{ pt: 1, a: { color: 'primary.dark', fontWeight: 500 } }}>
                {intl.formatMessage(
                  { id: 'experience.page.altSubTitle2' },
                  {
                    privacyMatters: <b>{intl.formatMessage({ id: 'experience.page.privacyMatters' })}</b>,
                    matchStatus: (
                      <b>{intl.formatMessage({ id: `profile.matchFeature.${matchFeatureValue ? 'on' : 'off'}` })}</b>
                    ),
                    link: <Link to={`${urlPrefix}/my-profile`}>{intl.formatMessage({ id: 'profile.title' })}</Link>,
                  }
                )}
              </Typography>
            </Alert>
          )}
        </Box>
        <Hidden smUp>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <AddNewExperienceMenu
              addNewExperience={handleAddExperience}
              clickAddFromWallet={() => setQrDialogOpen(true)}
            />
            <Tooltip
              enterTouchDelay={0}
              leaveTouchDelay={3000}
              title={
                <Fragment>
                  <span style={{ fontSize: 14 }}>{intl.formatMessage({ id: 'experience.mobileTooltip' })}</span>
                </Fragment>
              }
              placement="top"
              arrow
            >
              <img
                alt="Info"
                src={info}
                style={{
                  position: 'relative',
                  top: 1,
                  paddingLeft: 16,
                  width: '17px',
                  height: '17px',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          </Box>
        </Hidden>
        <Box
          sx={{
            mt: 1,
            display: { xs: 'none', sm: 'flex' },
          }}
        >
          <Button
            color="primary"
            size="large"
            variant="contained"
            startIcon={<AddCircleIcon sx={{ position: 'relative', bottom: 1 }} />}
            sx={{ mr: 2, textTransform: 'none', fontSize: 14 }}
            onClick={() => handleAddExperience('work')}
            id="add-new-experience-button"
          >
            {intl.formatMessage({ id: 'experience.page.addWorkExperience' })}
          </Button>

          <Button
            color="primary"
            size="large"
            variant="contained"
            startIcon={<AddCircleIcon sx={{ position: 'relative', bottom: 1 }} />}
            sx={{ mr: 2, textTransform: 'none', fontSize: 14 }}
            onClick={() => handleAddExperience('education')}
            id="add-new-experience-button"
          >
            {intl.formatMessage({ id: 'experience.page.addEducationExperience' })}
          </Button>

          {/* Desktop button */}
          <Button
            color="primary"
            size="large"
            variant="contained"
            startIcon={<QrCodeIcon sx={{ position: 'relative', bottom: 1 }} />}
            sx={{ textTransform: 'none', fontSize: 14 }}
            onClick={() => {
              setQrDialogOpen(true);
              trackEvent('EXPERIENCE_WALLET_ADD_BUTTON_CLICK');
            }}
          >
            {intl.formatMessage({ id: 'experience.page.shareFromWallet' })}
          </Button>
        </Box>
        <Box sx={{ mb: 4 }} />
        <div>
          <Box sx={{ margin: { xs: 2, sm: 3 } }} />
          <Hidden mdUp>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '209px',
              }}
            >
              <FormControl variant="filled" sx={{ mb: 2, minWidth: 120 }}>
                <InputLabel id="demo-simple-select-filled-label">
                  {intl.formatMessage({ id: 'experience.sort.sortBy' })}
                </InputLabel>
                <Select
                  labelId="demo-simple-select-filled-label"
                  id="demo-simple-select-filled"
                  value={sortBy}
                  onChange={(evt) => {
                    setSortBy(evt.target.value);
                    if (evt.target.value === 'numberSkills' || evt.target.value === 'default') {
                      setSortDirection('desc');
                    } else {
                      setSortDirection('asc');
                    }
                  }}
                >
                  <MenuItem value="default">{intl.formatMessage({ id: 'experience.sort.default' })}</MenuItem>
                  <MenuItem value="experience">{intl.formatMessage({ id: 'experience.sort.experience' })}</MenuItem>
                  <MenuItem value="description">{intl.formatMessage({ id: 'experience.sort.description' })}</MenuItem>
                  <MenuItem value="numberSkills">{intl.formatMessage({ id: 'experience.sort.numberSkills' })}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {experience.length > 0 ? (
              <MobilePaginatedDrawers
                replaceItem={replaceItem}
                experience={experience}
                deleteItem={deleteItem}
                saveMilitaryCourses={upsertMilitaryCourse}
                saveAcademicDegrees={upsertAcademicDegree}
              />
            ) : null}
          </Hidden>
          <Hidden mdDown>
            {experience.length > 0 ? (
              <ExperienceWalletDesktopTable
                experience={experience}
                setItemToEdit={setItemToEdit}
                deleteItem={deleteItem}
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDirection={sortDirection}
                setSortDirection={setSortDirection}
              />
            ) : null}
          </Hidden>
          <Box m={3} />
        </div>
      </Container>
      <Dialog open={showExperienceAlert} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {intl.formatMessage({ id: 'experience.firstVisitModal.title' })}
          <IconButton
            aria-label={intl.formatMessage({ id: 'experience.firstVisitModal.close' })}
            onClick={dismissExperienceAlert}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon titleAccess={intl.formatMessage({ id: 'experience.firstVisitModal.close' })} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ textAlign: 'left', whiteSpace: 'pre-wrap' }}>
            <FormattedMessage
              id="experience.firstVisitModal.body"
              values={{
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="outlined" onClick={dismissExperienceAlert} id="experience-first-visit-modal-close">
            {intl.formatMessage({ id: 'experience.firstVisitModal.close' })}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={newExperienceOpen || itemToEdit !== -1} onClose={() => {}}>
        <Box sx={{ px: 3, py: 2, maxWidth: 464, boxSizing: 'border-box' }}>
          <IconButton
            aria-label="Close"
            onClick={() => {
              setNewExperienceOpen(false);
              setItemToEdit(-1);
            }}
            sx={{
              position: 'absolute',
              right: 6,
              top: 6,
              color: 'black',
              fontSize: 34,
            }}
          >
            <CloseIcon />
          </IconButton>
          {(() => {
            // `itemToEdit` indexes the merged list (which includes JST rows), but the
            // form receives `nonJstExperience` and writes back with `currentIndex`.
            // Translate by id so the PUT payload overwrites the correct record.
            // Stable ids (set above in `mergedExperience`) are a precondition.
            const editedItem = itemToEdit !== -1 ? experience[itemToEdit] : null;
            const editedNonJstIndex =
              editedItem && (!editedItem.isJst || editedItem.sourceType === 'military')
                ? nonJstExperience.findIndex((x) => x.id === editedItem.id)
                : -1;
            return (
              <ExperienceWalletForm
                key={itemToEdit !== -1 ? `edit-${itemToEdit}` : `new-${formScope}`}
                formScope={editedItem?.sourceType || formScope}
                hasSelectedCategory={editedItem ? editedItem.sourceType === 'military' : hasSelectedCategory}
                onCategoryChange={editedItem ? undefined : () => setHasSelectedCategory(true)}
                appendItem={editedItem ? null : appendItem}
                replaceItem={editedItem ? replaceItem : null}
                currentIndex={editedNonJstIndex}
                experience={editedItem ?? emptyExperience}
                experiences={nonJstExperience}
                showMilitaryCourse={hasMilitaryService}
                saveMilitaryCourses={upsertMilitaryCourse}
                saveAcademicDegrees={upsertAcademicDegree}
                setCurrentContent={() => {
                  setNewExperienceOpen(false);
                  setItemToEdit(-1);
                }} // In this case we use this method to close the modal
              />
            );
          })()}
        </Box>
      </Dialog>

      <Dialog
        open={qrDialogOpen}
        onClose={() => {
          setQrDialogOpen(false);
          setRefetchCounter(refetchCounter + 1);
        }}
      >
        <ConnectCredWalletDialog
          onClose={() => {
            queryClient.invalidateQueries({ queryKey: ['/users/me/experience/'] });
            setQrDialogOpen(false);
          }}
        />
      </Dialog>

      <LoadingModal open={isDeleting} message={intl.formatMessage({ id: 'experience.deletingExperience' })} />
    </div>
  );
};

export function uuidv4() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

export default Experience;

const ToastLink = ({ title, onClick }) => {
  return (
    <Typography
      component="span"
      onClick={onClick}
      sx={{
        textDecoration: 'underline',
        fontSize: 'inherit',
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontWeight: 'inherit',
      }}
    >
      {title}
    </Typography>
  );
};

ToastLink.propTypes = {
  title: PropTypes.string,
  onClick: PropTypes.func,
};
